import type { CourseCertificateInput } from '@/types/courses'

/** The API refuses longer texts (400 on `certificate.text`). */
export const CERTIFICATE_TEXT_MAX = 5000

/** The variables the certificate text accepts, as typed by the owner. */
export const CERTIFICATE_VARIABLES = ['{nome}', '{assunto}', '{cargaHoraria}'] as const

/** The same for an event: `{evento}` is `{assunto}` under its own name, and `{data}` is the event's date. */
export const EVENT_CERTIFICATE_VARIABLES = ['{nome}', '{evento}', '{data}', '{cargaHoraria}'] as const

const ALIASES: Record<string, 'name' | 'subject' | 'workload' | 'date'> = {
  nome: 'name', name: 'name',
  assunto: 'subject', subject: 'subject', curso: 'subject', course: 'subject', evento: 'subject', event: 'subject',
  cargaHoraria: 'workload', workload: 'workload',
  data: 'date', date: 'date',
}

/** `date` is for events only: a course text has no `{data}`, so it stays as typed there. */
type Vars = Record<'name' | 'subject' | 'workload', string> & { date?: string }

/** '' when the variable is known and empty, `null` when the name is not a variable. */
const valueOf = (name: string, vars: Vars): string | null => {
  const key = ALIASES[name]
  if (!key) return null
  if (key === 'date') return vars.date ?? null
  return vars[key]
}

const hasEmptyVariable = (text: string, vars: Vars) =>
  [...text.matchAll(/\{(\w+)\}/g)].some(([, name]) => valueOf(name, vars) === '')

/**
 * A client copy of the API's description filler, so the preview prints what the PDF will:
 * text in `[brackets]` is optional and goes away when a variable inside it is empty, and a
 * comma- or semicolon-delimited clause with an empty variable is dropped (never the first).
 */
export const fillCertificateText = (text: string, vars: Vars): string => {
  let out = text.replace(/\[([^[\]]*)\]/g, (_match, inner: string) => (hasEmptyVariable(inner, vars) ? '' : inner))
  const pieces = out.split(/([,;])/)
  const kept: string[] = []
  for (let i = 0; i < pieces.length; i += 2) {
    const clause = pieces[i]
    const delimiter = pieces[i + 1] || ''
    if (i > 0 && hasEmptyVariable(clause, vars)) {
      kept.pop()
      const end = clause.match(/[.!?]\s*$/)
      if (end) kept.push(end[0].trim())
      if (delimiter) kept.push(delimiter)
      continue
    }
    kept.push(clause)
    if (delimiter) kept.push(delimiter)
  }
  out = kept.join('')
  return out.replace(/\{(\w+)\}/g, (match, name: string) => valueOf(name, vars) ?? match).replace(/\s{2,}/g, ' ').trim()
}

/** What the person decided in the form. `text: null` means "the default text". */
export interface CertificateDraft {
  enabled: boolean
  /** `null` is the platform default. */
  templateId: string | null
  text: string | null
}

export const emptyCertificateDraft = (): CertificateDraft => ({ enabled: false, templateId: null, text: null })

/** The body the API wants. Text equal to the default is sent as `null`, so it keeps following it. */
export const certificatePayload = (draft: CertificateDraft, defaultText: string): CourseCertificateInput => {
  if (!draft.enabled) return { enabled: false }
  const text = draft.text !== null && draft.text.trim() && draft.text !== defaultText ? draft.text : null
  return { enabled: true, templateId: draft.templateId, text }
}

/** Hours as the certificate prints them: at most two decimals, in the person's language. */
export const formatHours = (hours: number, language: string): string =>
  new Intl.NumberFormat(language, { maximumFractionDigits: 2 }).format(hours)

/**
 * `{cargaHoraria}` as the API prints it: the number plus its unit, singular or plural by the
 * language's own plural rule ("0,36 hora", "2 horas", "1 hour").
 */
export const formatHoursWithUnit = (hours: number, language: string, units: { one: string; other: string }): string => {
  let singular = false
  try { singular = new Intl.PluralRules(language).select(hours) === 'one' } catch { /* plural unit */ }
  return `${formatHours(hours, language)} ${singular ? units.one : units.other}`
}

/**
 * Workload for display ("0,98 hora", "2 horas"): the number in the viewer's format, the unit
 * picked by i18next's plural rule for `count` (same Intl.PluralRules the server uses).
 * `t` must be bound to the `certificates` namespace.
 */
export const formatWorkload = (
  t: (key: string, options: Record<string, unknown>) => string,
  hours: number,
  language: string
): string => t('detail.workloadValue', { count: hours, value: formatHours(hours, language) })
